import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Pencil, RotateCcw } from "lucide-react";
import { ACTION_IDS, ACTION_LABELS, DEFAULT_SHORTCUTS, formatComboForDisplay, loadCurrentShortcuts, resetAllShortcuts, resetShortcut, saveShortcut, } from "./shortcuts";
import ShortcutCaptureInput from "./ShortcutCaptureInput";
export default function SettingsPanel() {
    const [resetStatus, setResetStatus] = useState("idle");
    const [currentShortcuts, setCurrentShortcuts] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [rowErrors, setRowErrors] = useState({
        TracePrecedents: null,
        TraceDependents: null,
        OpenGridrive: null,
    });
    const refresh = async () => {
        const s = await loadCurrentShortcuts();
        setCurrentShortcuts(s);
    };
    useEffect(() => {
        void refresh();
    }, []);
    const setRowError = (id, err) => {
        setRowErrors((prev) => ({ ...prev, [id]: err }));
    };
    const handleCapture = async (id, combo) => {
        setEditingId(null);
        const result = await saveShortcut(id, combo);
        if (result.ok) {
            setRowError(id, null);
            setCurrentShortcuts((prev) => (prev ? { ...prev, [id]: combo } : prev));
        }
        else {
            setRowError(id, result.reason);
        }
    };
    const handleResetRow = async (id) => {
        setRowError(id, null);
        try {
            await resetShortcut(id);
            await refresh();
        }
        catch {
            setRowError(id, "error");
        }
    };
    const handleResetAll = async () => {
        try {
            const ok = await resetAllShortcuts();
            setResetStatus(ok ? "done" : "error");
            if (ok)
                await refresh();
        }
        catch {
            setResetStatus("error");
        }
    };
    const renderComboCell = (id) => {
        if (editingId === id) {
            return (_jsx(ShortcutCaptureInput, { onCapture: (combo) => handleCapture(id, combo), onCancel: () => setEditingId(null) }));
        }
        const current = currentShortcuts?.[id] ?? null;
        const display = current ? formatComboForDisplay(current) : "—";
        return (_jsx("kbd", { className: "rounded bg-muted px-2 py-0.5 font-mono text-xs text-muted-foreground", children: display }));
    };
    const errorMessage = (err) => {
        switch (err) {
            case "invalid":
                return "Invalid — need a modifier plus a key";
            case "unsupported":
                return "Not supported on this platform";
            case "notPersisted":
                return "Saved — reload Excel for it to take effect";
            case "error":
                return "Could not save — try again";
            default:
                return null;
        }
    };
    return (_jsx("div", { className: "flex h-full flex-col bg-background", children: _jsxs("div", { className: "flex-1 overflow-y-auto p-3", children: [_jsxs("div", { className: "mb-3", children: [_jsx("h4", { className: "mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground", children: "Keyboard Shortcuts" }), _jsx(Card, { children: _jsxs("div", { className: "divide-y divide-border", children: [ACTION_IDS.map((id) => {
                                        const current = currentShortcuts?.[id] ?? null;
                                        const isDefault = current === DEFAULT_SHORTCUTS[id];
                                        const err = rowErrors[id];
                                        const errMsg = errorMessage(err);
                                        return (_jsxs("div", { className: "px-3 py-2", children: [_jsxs("div", { className: "flex items-center justify-between gap-2", children: [_jsx("span", { className: "text-sm text-foreground", children: ACTION_LABELS[id] }), _jsxs("div", { className: "flex items-center gap-1.5", children: [renderComboCell(id), _jsx(Button, { variant: "ghost", size: "icon-xs", onClick: () => {
                                                                        setRowError(id, null);
                                                                        setEditingId(id);
                                                                    }, title: "Change", disabled: editingId === id || currentShortcuts === null, children: _jsx(Pencil, { className: "size-3" }) }), _jsx(Button, { variant: "ghost", size: "icon-xs", onClick: () => handleResetRow(id), title: "Restore default", disabled: isDefault || currentShortcuts === null, children: _jsx(RotateCcw, { className: "size-3" }) })] })] }), errMsg && _jsx("p", { className: "mt-1 text-[11px] text-destructive", children: errMsg })] }, id));
                                    }), _jsxs("div", { className: "flex items-center justify-between px-3 py-2.5", children: [_jsx("span", { className: "text-sm text-foreground", children: "Close Add-in" }), _jsx("kbd", { className: "rounded bg-muted px-2 py-0.5 font-mono text-xs text-muted-foreground", children: "Esc" })] })] }) })] }), _jsxs("div", { className: "mb-3", children: [_jsx("h4", { className: "mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground", children: "Troubleshooting" }), _jsx(Card, { children: _jsxs("div", { className: "p-3", children: [_jsx("p", { className: "text-xs text-muted-foreground", children: "If shortcuts aren't working, Excel may have assigned them to another action." }), _jsx(Button, { variant: "outline", size: "sm", className: "mt-2 w-full", onClick: handleResetAll, disabled: resetStatus === "done", title: 'If this doesn\'t work, search "Reset Office Add-in Shortcut Preferences" in the Tell Me bar at the top of the Excel ribbon.', children: resetStatus === "done"
                                            ? "Shortcuts restored"
                                            : resetStatus === "error"
                                                ? "Not supported — remove & re-add the add-in"
                                                : "Restore all to defaults" }), _jsxs("p", { className: "mt-1.5 text-[10px] leading-tight text-muted-foreground/70", children: ["If this doesn't work, search ", _jsx("strong", { children: "\"Reset Office Add-in Shortcut Preferences\"" }), " in the Tell Me bar at the top of the Excel ribbon."] })] }) })] })] }) }));
}
